import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';

const navLinks = [
  { label: 'Home', to: '/', exact: true },
  { label: 'HRMS', to: '/hrms' },
  { label: 'Web Dev', to: '/web-dev' },
  { label: 'Recruitment', to: '/recruitment' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 50);
  });

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-bg-primary/80 backdrop-blur-md border-b border-white/5'
            : 'bg-transparent'
        }`}
        style={{ height: '72px' }}
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-center justify-between gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" onClick={closeMenu}>
            <img src="/trivologo.png" alt="Trivo Logo" className="h-8 w-auto" />
            <span className="font-body text-sm text-text-muted font-medium hidden sm:inline mt-1">
              Talent Trend Solutions
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <ul className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.exact}
                  className={({ isActive }) =>
                    `relative px-4 py-2 rounded-lg text-sm font-medium font-body transition-colors duration-200 group
                    ${isActive
                      ? 'text-accent-teal'
                      : 'text-text-muted hover:text-text-primary'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className="relative z-10">{link.label}</span>
                      {isActive && (
                        <motion.span
                          layoutId="nav-underline"
                          className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-accent-teal rounded-full"
                          transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        />
                      )}
                      <motion.span
                        className="absolute inset-0 rounded-lg bg-white/5 opacity-0 group-hover:opacity-100"
                        transition={{ duration: 0.15 }}
                      />
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* CTA + Burger */}
          <div className="flex items-center gap-3">
            <motion.div
              className="hidden lg:block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent-teal text-bg-primary font-semibold text-sm btn-glow-teal transition-all duration-300"
              >
                Get Started
              </Link>
            </motion.div>

            {/* Burger button */}
            <motion.button
              className="lg:hidden w-10 h-10 flex flex-col justify-center items-center gap-1.5 rounded-xl hover:bg-white/5 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              whileTap={{ scale: 0.95 }}
            >
              <motion.span
                className="block w-5 h-0.5 bg-text-primary rounded-full origin-center"
                animate={menuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.25 }}
              />
              <motion.span
                className="block w-5 h-0.5 bg-text-primary rounded-full"
                animate={menuOpen ? { opacity: 0, scaleX: 0 } : { opacity: 1, scaleX: 1 }}
                transition={{ duration: 0.2 }}
              />
              <motion.span
                className="block w-5 h-0.5 bg-text-primary rounded-full origin-center"
                animate={menuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
                transition={{ duration: 0.25 }}
              />
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed inset-0 bg-bg-primary/60 backdrop-blur-sm z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeMenu}
          />
        )}
      </AnimatePresence>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="fixed top-0 right-0 bottom-0 w-72 max-w-[85vw] bg-bg-secondary border-l border-white/8 z-50 flex flex-col p-6 gap-6 lg:hidden overflow-y-auto"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
                <img src="/trivologo.png" alt="Trivo Logo" className="h-8 w-auto" />
              </Link>
              <button
                onClick={closeMenu}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary rounded-lg hover:bg-white/5 transition-colors"
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            {/* Drawer Links */}
            <ul className="flex flex-col gap-1">
              {navLinks.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.3 }}
                >
                  <NavLink
                    to={link.to}
                    end={link.exact}
                    className={({ isActive }) =>
                      `block px-4 py-3.5 rounded-xl text-base font-medium font-body transition-all duration-200
                      ${isActive
                        ? 'text-accent-teal bg-accent-teal/10'
                        : 'text-text-muted hover:text-text-primary hover:bg-white/5'
                      }`
                    }
                    onClick={closeMenu}
                  >
                    {link.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>

            <motion.div
              className="mt-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
            >
              <Link
                to="/contact"
                className="block w-full text-center px-5 py-3 rounded-full bg-accent-teal text-bg-primary font-semibold btn-glow-teal transition-all"
                onClick={closeMenu}
              >
                Get Started
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
